/**
 * Thin wrapper around the backend Nafath endpoints. Each call returns the
 * raw payload; the calling page handles state + polling.
 *
 * Backend gates every endpoint on NAFATH_APP_ID being set. When it's not,
 * /me returns `available: false` and the page falls back to the existing
 * "Coming Soon" UI from <NafathVerification />.
 */

import { apiRequest } from '../utils/apiClient';

export interface NafathInitiateResponse {
  transId: string;
  random: string;
  expiresAt: string;
}

export interface NafathStatusResponse {
  // Uppercase from the Elm API: WAITING | COMPLETED | REJECTED | EXPIRED.
  status: 'WAITING' | 'COMPLETED' | 'REJECTED' | 'EXPIRED';
  verified: boolean;
  // Chained FAL outcome, set on COMPLETED when the broker already has a
  // license number on file.
  falStatus: 'APPROVED' | 'PENDING' | null;
}

export interface NafathMeResponse {
  available: boolean;
  nafathVerificationStatus: 'NOT_STARTED' | 'PENDING' | 'VERIFIED' | 'FAILED' | 'EXPIRED';
  nafathVerifiedAt: string | null;
  nafathRejectionReason: string | null;
  nationalIdLastFour: string | null;
  falVerificationStatus: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
  licenseNumber: string | null;
}

export async function initiateNafath(
  nationalId: string,
  locale: 'ar' | 'en',
  token: string,
): Promise<NafathInitiateResponse> {
  return apiRequest<NafathInitiateResponse>('/nafath/initiate', {
    method: 'POST',
    body: { nationalId, locale },
    token,
  });
}

export async function checkNafathStatus(
  transId: string,
  token: string,
): Promise<NafathStatusResponse> {
  return apiRequest<NafathStatusResponse>('/nafath/status', {
    method: 'POST',
    body: { transId },
    token,
  });
}

export async function fetchNafathMe(token: string): Promise<NafathMeResponse> {
  return apiRequest<NafathMeResponse>('/nafath/me', { token });
}
